import React, { useEffect, useRef } from "react";
import { renderAsync } from "docx-preview";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css"; // Core styles
import "@react-pdf-viewer/default-layout/lib/styles/index.css"; // Default layout styles
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { searchPlugin } from "@react-pdf-viewer/search";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import { useState } from "react";
import { userSelector } from "../user-redux/selector";
import { useSelector } from "react-redux";
import "../styles/StylesPdfViewer.css";
import { appSelector } from "../redux/selector";
import Key from "../../clientVariables.json";
import { useTheme, useMediaQuery, Box, IconButton, Tooltip, Typography, InputBase } from '@mui/material';
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";




// Define your custom theme
const customTheme = {
  viewer: {
    backgroundColor: "#fff", // Change the background color of the viewer
  },
  toolbar: {
    backgroundColor: "#f5f5f5",
    color: "#333 !important",
  },
  // You can add more customizations here for different parts of the layout
};

const RpvFix = createGlobalStyle`
  /* Let the popover escape the viewer’s internal wrappers */
  .rpv-default-layout__container,
  .rpv-default-layout__main,
  .rpv-default-layout__body,
  .rpv-default-layout__toolbar,
  .rpv-toolbar {
    overflow: visible !important;
  }

  /* Make the popover stack above MUI drawers/dialogs */
  .rpv-core__popover,
  .rpv-search__popover,
  .rpv-core__popover-body {
    z-index: 2000 !important;
  }

  /* Defensive: remove stacking-context creators */
  .rpv-default-layout__container,
  .rpv-default-layout__toolbar {
    transform: none !important;
    filter: none !important;
    perspective: none !important;
    contain: none !important;
  }
`;

// Style the Viewer component using styled-components
const StyledViewer = styled(Viewer)`
  background-color: ${({ theme }) => theme.viewer.backgroundColor};
`;

const DocxViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    flex: 1;
  }
`;

const DocxToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background-color: #ffffff;
  color: #333333;
  z-index: 1000;
  min-height: 48px;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const DocxContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 20px;
  background-color: #ffffff;
  
  /* Custom selection color for search highlighting */
  & ::selection {
    background: #ffff00 !important;
    color: #000 !important;
  }

  .docx-wrapper {
    background-color: #ffffff !important;
    padding: 0 !important;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  article.docx {
    background-color: #ffffff !important;
    box-shadow: 0 0 10px rgba(0,0,0,0.1) !important;
    margin-bottom: 30px !important;
    padding: 50px !important;
    width: 850px !important;
    min-height: 1000px !important;
    color: #000000 !important;
  }
`;

const ResumePdfViewer = ({ resume_id, file_name }) => {
  // Initialize the plugins
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [],
  });
  const searchPluginInstance = searchPlugin();
  const toolbarPluginInstance = toolbarPlugin();
  const zoomPluginInstance = zoomPlugin();
  const [defaultFile, setDefaultFile] = useState();
  const { user } = useSelector(userSelector);
  const { candidateSelectedResume, selectedCandidate, candidateDetails } =
    useSelector(appSelector);
  const [fileType, setFileType] = useState('');
  const [zoom, setZoom] = useState(1.0);
  const [searchTerm, setSearchTerm] = useState("");
  const docxRef = useRef(null);

  const theme = useTheme();
  // Define breakpoints for different screen sizes
  const isMobileSmall = useMediaQuery(theme.breakpoints.down("xs")); // Very small mobile devices
  const isMobile = useMediaQuery(theme.breakpoints.between("xs", "sm")); // Regular mobile devices
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablets
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // Desktops and larger
  const isDesktopSmall = useMediaQuery(theme.breakpoints.between("md", "lg")); // Small desktops
  const isDesktopLarge = useMediaQuery(theme.breakpoints.up("lg")); // Large desktops

  useEffect(() => {
    console.log("candidatesPage ResumePdfViewer - Props changed:", { resume_id, file_name });
    console.log("candidatesPage ResumePdfViewer - Selected Candidate:", selectedCandidate);
    if (resume_id && file_name) {
      const extension = file_name.split('.').pop().toLowerCase();
      setFileType(extension);
      setSearchTerm(""); // Clear search when switching resume
      const fileUrl = `${Key.domain}/candidate/get-resume/root/data/bench_data/attachments/cand_${candidateDetails?.candidate_id}/${file_name}`;
      setDefaultFile(fileUrl);
      console.log("candidatesPage ResumePdfViewer - Constructed File URL:", fileUrl);
    }
  }, [resume_id, file_name, selectedCandidate]);

  useEffect(() => {
    if (fileType === 'docx' && defaultFile && docxRef.current) {
      console.log("candidatesPage ResumePdfViewer - Starting DOCX render for:", defaultFile);
      fetch(defaultFile)
        .then((response) => {
          console.log("candidatesPage ResumePdfViewer - Fetch response status:", response.status);
          if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
          return response.arrayBuffer();
        })
        .then((buffer) => {
          console.log("candidatesPage ResumePdfViewer - File buffer loaded, length:", buffer.byteLength);
          docxRef.current.innerHTML = "";
          return renderAsync(buffer, docxRef.current, null, {
            className: "docx",
            inWrapper: true,
            ignoreLastRenderedPageBreak: false,
          });
        })
        .then(() => {
          console.log("candidatesPage ResumePdfViewer - DOCX render completed successfully");
          applyZoom();
        })
        .catch((error) => {
          console.error("candidatesPage ResumePdfViewer - Error in DOCX rendering pipeline:", error);
        });
    } else {
      console.log("candidatesPage ResumePdfViewer - Render criteria not met:", { fileType, hasFile: !!defaultFile, hasRef: !!docxRef.current });
    }
  }, [fileType, defaultFile]);

  const applyZoom = () => {
    if (docxRef.current) {
      const wrapper = docxRef.current.querySelector('.docx-wrapper');
      if (wrapper) {
        wrapper.style.zoom = zoom;
      } else {
        docxRef.current.style.zoom = zoom;
      }
    }
  };

  useEffect(() => {
    applyZoom();
  }, [zoom]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = defaultFile;
    link.download = file_name || "document.docx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2.0));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchTerm) {
      window.find(searchTerm, false, false, true);
    }
  };

  const executeSearch = () => {
    if (searchTerm) {
      window.find(searchTerm, false, false, true);
    }
  };

  const renderViewer = () => {
    if (!defaultFile) return <div>Loading...</div>;

    if (fileType === 'pdf') {
      return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <StyledViewer
            fileUrl={defaultFile}
            plugins={[
              defaultLayoutPluginInstance,
              searchPluginInstance,
              toolbarPluginInstance,
              zoomPluginInstance,
            ]}
          />
        </Worker>
      );
    } else if (fileType === 'docx') {
      return (
        <DocxViewerContainer>
          <DocxToolbar>
            {/* Left: Search */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                bgcolor: '#f1f3f4', 
                borderRadius: 1, 
                px: 1,
                width: 200
              }}>
                <SearchIcon fontSize="small" sx={{ color: "#5f6368", mr: 1 }} />
                <InputBase
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearch}
                  sx={{ color: '#3c4043', fontSize: '0.875rem', width: '100%' }}
                />
                <IconButton onClick={executeSearch} size="small" sx={{ color: "#5f6368" }}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Middle: Zoom */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1
              }}>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={handleZoomOut} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ color: "#3c4043", minWidth: 50, textAlign: 'center', fontWeight: 500 }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton onClick={handleZoomIn} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Right: Download */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Download">
                <IconButton onClick={handleDownload} size="small" sx={{ 
                  color: "#5f6368",
                  '&:hover': { bgcolor: '#f1f3f4' }
                }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </DocxToolbar>
          <DocxContent ref={docxRef} />
        </DocxViewerContainer>
      );
    } else if (fileType === "doc") {
      // Fallback for legacy .doc files with consistent toolbar (Search hidden as not supported)
      return (
        <DocxViewerContainer>
          <DocxToolbar>
            {/* Left: Empty (Search not supported for .doc) */}
            <Box sx={{ flex: 1 }} />

            {/* Middle: Zoom (CSS Scale for .doc iframe) */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1
              }}>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={handleZoomOut} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ color: "#3c4043", minWidth: 50, textAlign: 'center', fontWeight: 500 }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton onClick={handleZoomIn} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Right: Download */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Download">
                <IconButton onClick={handleDownload} size="small" sx={{ 
                  color: "#5f6368",
                  '&:hover': { bgcolor: '#f1f3f4' }
                }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </DocxToolbar>
          <Box sx={{ 
            flex: 1, 
            width: '100%', 
            overflow: 'auto', 
            display: 'flex', 
            justifyContent: 'center',
            bgcolor: '#f0f2f5',
            p: 2
          }}>
            <Box sx={{
              width: '850px',
              height: 'fit-content',
              minHeight: '100%',
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease-in-out',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)'
            }}>
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(defaultFile)}`}
                title="Document Viewer"
                style={{ width: '100%', height: '1000px', border: 'none' }}
              />
            </Box>
          </Box>
        </DocxViewerContainer>
      );
    } else {
      return <div>Unsupported file type. <a href={defaultFile} target="_blank" rel="noopener noreferrer">Download file</a></div>;
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <RpvFix />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
        {renderViewer()}
      </Box>
    </ThemeProvider>
  );
};

export default ResumePdfViewer;
